import React from "react"
import { Link } from "react-router-dom"
import {ArrowRight, CheckCircle, Truck, Shield, Wrench} from 'lucide-react'
import { motion } from "framer-motion"

const HomePage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://static.lumi.new/36/3661be1428baa055a82e994af7fba1ac.png')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/95 via-zinc-950/80 to-zinc-950/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Professional <span className="text-orange-500">Mule Shed</span><br />
              Moving Equipment Information
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl">
              Comprehensive information about <strong>shed moving equipment</strong> for portable building movers and professional haulers. Learn about high-performance machinery designed to move heavy loads with precision.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/products" 
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl shadow-orange-500/30 flex items-center justify-center gap-2"
              >
                Learn About Equipment <ArrowRight />
              </Link>
              <Link 
                to="/buying-guide" 
                className="bg-transparent border-2 border-white hover:bg-white hover:text-zinc-900 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2"
              >
                Buying Guide
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-gray-400 rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Understanding <span className="text-orange-500">Shed Moving Mule</span> Technology
              </h2>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                MuleShed.com provides comprehensive information about professional-grade <strong>mule moving shed</strong> equipment designed for the toughest hauling jobs. Learn about machinery specifications and capabilities for portable building movers nationwide.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={24} />
                  <p className="text-gray-300">Information on hydraulic precision control for safe <strong>mule to move shed</strong> operations</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={24} />
                  <p className="text-gray-300">Details on heavy-duty construction for commercial shed hauling</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={24} />
                  <p className="text-gray-300">Educational resources about <strong>mule shed delivery</strong> logistics and processes</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://static.lumi.new/6f/6f1fd98ff6a6e825cc7a43a71b83549d.png" 
                alt="Shed moving mule in operation"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white p-6 rounded-lg shadow-xl">
                <p className="text-3xl font-bold">25+</p>
                <p className="text-sm">Years of Industry History</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Key Features of <span className="text-orange-500">Shed Moving Equipment</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Learn about the specifications and capabilities that define professional-grade shed moving machinery.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-zinc-900 p-8 rounded-lg border border-zinc-800 hover:border-orange-500 transition-all hover:transform hover:scale-105"
            >
              <Truck className="text-orange-500 mb-4" size={48} />
              <h3 className="text-xl font-bold text-white mb-3">Professional Grade Specifications</h3>
              <p className="text-gray-400">
                Information about commercial-quality construction standards used in equipment designed for professional shed haulers and building movers.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-zinc-900 p-8 rounded-lg border border-zinc-800 hover:border-orange-500 transition-all hover:transform hover:scale-105"
            >
              <Shield className="text-orange-500 mb-4" size={48} />
              <h3 className="text-xl font-bold text-white mb-3">Durability Features</h3>
              <p className="text-gray-400">
                Detailed information on heavy-duty steel frame construction and industrial hydraulic systems that provide reliable performance in demanding conditions.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-zinc-900 p-8 rounded-lg border border-zinc-800 hover:border-orange-500 transition-all hover:transform hover:scale-105"
            >
              <Wrench className="text-orange-500 mb-4" size={48} />
              <h3 className="text-xl font-bold text-white mb-3">Industry Resources</h3>
              <p className="text-gray-400">
                Educational content about training requirements, maintenance schedules, and technical specifications for shed moving operations.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Learn More About Equipment Costs
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Explore detailed information about <strong>shed mule cost</strong> factors, pricing considerations, and what influences equipment investment decisions.
          </p>
          <Link 
            to="/blog" 
            className="inline-block bg-white hover:bg-gray-100 text-orange-600 px-10 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl"
          >
            Explore Educational Resources
          </Link>
        </div>
      </section>
    </div>
  )
}

export default HomePage
